/**
 * Local persistence for spec-019 baked layered-mockup assets, keyed by template
 * VERSION id. The editor bakes them once (green-spill shell + product mask + the
 * generated image + profile/print-quad) at lock time; the render queue resolves them
 * back at render time so a locked template composites design-over-shell deterministically
 * — even in a later session, and without re-running any ML (Constitution III).
 *
 * IndexedDB (not localStorage) because the assets are multi-MB binary; it survives app
 * auto-updates. Mirrors `modelStore.ts`. Bytes stay on the client (Constitution VII);
 * the cloud holds only the metadata refs (see catalog `baked_assets`).
 *
 * ENCRYPTED AT REST: the image byte fields are AES-GCM encrypted before they touch
 * IndexedDB (see blobCrypto), so the on-disk mockup is ciphertext. The in-memory cache
 * keeps the plaintext for fast render resolution — encryption protects the disk copy, not
 * RAM. Legacy plaintext rows (written before this) still read back (the `enc` flag gates it).
 */
import { withPlatformLight } from "./segmenterCalibration.js";
import type { MockupAssets, Polygon, VarnishParams } from "@genz/engine-bind";
import { getBlobKey, encryptBytes, decryptBytes } from "./blobCrypto.js";
import { getMockupMemBudgetMb } from "./memoryBudget.js";
import { PRIMARY_VIEW } from "../templates/views.js";

const DB = "genz-mockups";
const STORE = "baked";

/**
 * The mem/IndexedDB key for one template version's baked assets in a given view (Task B2).
 * Multi-view templates bake per view, so a bare `versionId` no longer identifies a single
 * mockup. Pre-B2 rows were written under the BARE `versionId` (all primary/back view) — see
 * the legacy fallback in {@link getMockup}. The IDB DB name/version are unchanged; keys are
 * just strings, so no store migration is needed.
 */
export function mockupKey(versionId: string, view: string): string {
  return `${versionId}::${view}`;
}
/** The persisted byte fields to encrypt at rest (the actual mockup pixels). */
const BYTE_FIELDS = ["generated", "shell", "productMask", "printMask", "phone", "texture"] as const;

/**
 * Is this value a byte buffer? (review-1 M-6)
 *
 * `v instanceof Uint8Array` is REALM-SCOPED: a typed array that crossed a realm boundary — an
 * IndexedDB structured clone under jsdom today, a worker or iframe in a future build — fails it
 * even though it IS a `Uint8Array`. Every use below gates a byte pass, so a miss is silent and
 * wrong in the worst way: `decStored` hands back CIPHERTEXT as if it were plaintext, `encStored`
 * writes plaintext to disk, and `sizeOf` counts a multi-MB raster as 0 against the LRU budget.
 * `ArrayBuffer.isView` asks the question without asking which realm made it. `DataView` is the
 * one view that is not a byte array, so it is excluded.
 */
function isBytes(v: unknown): v is Uint8Array {
  return ArrayBuffer.isView(v) && !(v instanceof DataView);
}

/** The persisted shape (MockupAssets minus brand/background, which are per-render inputs).
 *  NB: opacity, blendMode and designRotation are BAKED template properties — they must be
 *  persisted here or the batch/export render (which resolves assets via getMockup) silently
 *  ignores the placement rotation, per-layer opacity and texture blend the editor saved. */
interface StoredMockup {
  generated: Uint8Array;
  shell: Uint8Array;
  productMask: Uint8Array;
  /** Print-area mask (`w*h`, 255 inside the print region) — confines the design to the
   *  print area on a clear case. Optional (older bakes won't have it → legacy clip). */
  printMask?: Uint8Array;
  printQuad: Polygon;
  clearPhone: boolean;
  phone?: Uint8Array | null;
  texture?: Uint8Array | null;
  opacity?: MockupAssets["opacity"];
  blendMode?: MockupAssets["blendMode"];
  /** Spot-UV varnish strength (0..1) for a "uvvarnish" texture blend — a baked template
   *  property, so it must persist here or the batch/export render drops the varnish. */
  varnish?: VarnishParams;
  designRotation?: number;
  fillFit?: MockupAssets["fillFit"];
  designAspect?: MockupAssets["designAspect"];
  /** Effective artwork shading strength (already `stageShading ? strength : 0`, computed at
   *  bake time) — a baked template property, so it must persist here or the batch/export
   *  render loses the shading. */
  printShadingStrength?: number;
  /** Full engine shading calibration (wins over the scalar when present) — a baked template
   *  property for the same reason. */
  shading?: MockupAssets["shading"];
}

/**
 * In-memory plaintext bake cache — now a byte-budgeted LRU (WS-H2 / AUD-3 F-15 / LB-2).
 *
 * It used to be an unbounded `Map` written on every put and every miss-then-load, emptied only
 * by an explicit delete or a sign-out purge. At ~13 MB per entry, browsing 200 templates × 3
 * views is ~7.8 GB resident and the renderer is killed mid-batch ("it crashed again, nothing
 * saved").
 *
 * `Map` iterates in INSERTION order, so delete-then-set is a recency bump and the FRONT of the
 * map is the least-recently-used — no second data structure for ordering.
 *
 * `present` is NOT the cache: it records which keys this session knows exist LOCALLY (memory or
 * IndexedDB) and eviction never touches it. `hasMockup` answers from it, because `bakedSync.ts`
 * treats a `false` as "not downloaded yet" and would re-fetch multi-MB assets the device already
 * has, and `MaskEditor`'s / `TemplateEditor`'s `hasBake()` would offer to re-bake a template that
 * is baked. Evicting a VALUE is free (it is re-read from IndexedDB); evicting the KNOWLEDGE is a
 * bug.
 */
const mem = new Map<string, StoredMockup>();
const present = new Set<string>();
let memBytes = 0;

function sizeOf(s: StoredMockup): number {
  let n = 0;
  for (const f of BYTE_FIELDS) {
    const v = s[f];
    if (isBytes(v)) n += v.byteLength;
  }
  return n;
}

/** Write a value and evict the least-recently-used entries until the budget holds again. */
function memSet(key: string, s: StoredMockup): void {
  memDelete(key, { keepPresence: true });
  mem.set(key, s);
  memBytes += sizeOf(s);
  present.add(key);
  const budget = getMockupMemBudgetMb() * 1024 * 1024;
  for (const k of mem.keys()) {
    if (memBytes <= budget || mem.size <= 1) break; // never evict the entry just written
    memDelete(k, { keepPresence: true }); // the IndexedDB row survives — see getMockup
  }
}

/** Read a value and refresh its recency (delete + set keeps the Map's insertion order honest). */
function memGet(key: string): StoredMockup | null {
  const s = mem.get(key);
  if (!s) return null;
  mem.delete(key);
  mem.set(key, s);
  return s;
}

/** Drop a value. `keepPresence` is for EVICTION, which must not touch the key-presence knowledge
 *  (`hasMockup`); an explicit delete passes it through so the key really goes (ED-12). */
function memDelete(key: string, opts: { keepPresence?: boolean } = {}): void {
  const s = mem.get(key);
  if (s) {
    memBytes -= sizeOf(s);
    if (memBytes < 0) memBytes = 0;
    mem.delete(key);
  }
  if (!opts.keepPresence) present.delete(key);
}

function memClear(): void {
  mem.clear();
  present.clear();
  memBytes = 0;
}

/** Cache occupancy, for the LRU tests and the Settings readout. */
export function memStats(): { entries: number; bytes: number; budget: number } {
  return { entries: mem.size, bytes: memBytes, budget: getMockupMemBudgetMb() * 1024 * 1024 };
}

/**
 * WS-ERR F-9 — why this device is keeping mock-ups in RAM only, or null when it is not.
 *
 * Making a failed write REJECT was right: a write that did not happen must not answer like one
 * that did. Letting that rejection travel was not — a private Firefox/Safari window, blocked
 * site data or a full disk would then fail the hydrate and the whole run, where the old code
 * carried on. The bytes are in the byte-budgeted memory cache either way, so the SESSION works;
 * it just will not survive a restart. That is a degrade, and a degrade has to be SAID, which is
 * what this is for (`mockups/BatchContents.tsx` renders it).
 *
 * Set once per session: the banner is a state, not a stream.
 */
let degraded: string | null = null;
/** Subscribers to the degrade record — the UI cannot poll a module variable. */
const degradedSubs = new Set<() => void>();

/**
 * Subscribe to the degrade record. `useSyncExternalStore`-shaped so a banner re-renders the
 * moment the first failed write records one, rather than only on whatever unrelated state
 * change happens to re-render its host next.
 */
export function subscribeMockupStoreDegraded(cb: () => void): () => void {
  degradedSubs.add(cb);
  return () => degradedSubs.delete(cb);
}

/** The reason mock-ups are memory-only on this device, or null while the store is healthy. */
export function mockupStoreDegraded(): string | null {
  return degraded;
}

/** Test seam — clears the one-shot record. Not called by the app. */
export function clearMockupStoreDegraded(): void {
  degraded = null;
  for (const cb of [...degradedSubs]) {
    try { cb(); } catch { /* … */ }
  }
}

/** Record a store fault ONCE, and answer with the reason. */
function noteDegraded(e: unknown): string {
  const reason = e instanceof Error ? e.message : String(e);
  if (degraded === null) {
    degraded = reason;
    for (const cb of [...degradedSubs]) {
      try { cb(); } catch { /* one bad subscriber must not stop the others */ }
    }
  }
  return reason;
}

function idb(): Promise<IDBDatabase | null> {
  return new Promise((resolve, reject) => {
    // No IndexedDB in this environment at all (jsdom, a locked-down embedding). That is a
    // CAPABILITY fact, not a failure: the byte-budgeted memory cache is the whole store for
    // this session, and every reader resolves from it.
    if (typeof indexedDB === "undefined") return resolve(null);
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    // …but an open that FAILS is a failure, and it used to resolve `null` — indistinguishable
    // from "this runtime has no IndexedDB", so `putMockup` returned as if the bake were on
    // disk. It was not, and the next launch rendered design-only.
    req.onerror = () => reject(req.error ?? new Error("mockup store could not be opened"));
    req.onblocked = () => reject(new Error("mockup store open blocked by another tab"));
  });
}

function idbGet(db: IDBDatabase, key: string): Promise<(StoredMockup & { enc?: true }) | null> {
  return new Promise((resolve) => {
    const r = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
    r.onsuccess = () => resolve((r.result as StoredMockup & { enc?: true }) ?? null);
    r.onerror = () => resolve(null);
  });
}

function idbPut(db: IDBDatabase, key: string, val: StoredMockup & { enc?: true }): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => resolve();
    // Silent-failure sweep: this used to `resolve()` on error, so a quota-exceeded or aborted
    // write reported SUCCESS. The bake was then believed to be on disk — the templates list
    // said "Synced", `hasMockup` said yes — and the batch rendered the design with no case
    // shell behind it. Exactly the class `stores/blobCrypto.ts` was already fixed for: a write
    // that did not happen must not answer like one that did.
    tx.onerror = () => reject(tx.error ?? new Error("mockup write failed"));
    tx.onabort = () => reject(tx.error ?? new Error("mockup write aborted (quota?)"));
  });
}

/** Encrypt the image byte fields before they touch disk. Falls back to plaintext when no
 *  crypto key is available (never blocks a save). */
async function encStored(s: StoredMockup): Promise<StoredMockup & { enc?: true }> {
  const key = await getBlobKey();
  if (!key) return s; // WebCrypto unavailable → store plaintext (best-effort)
  const out = { ...s, enc: true as const } as StoredMockup & { enc?: true };
  const rec = out as unknown as Record<string, unknown>;
  for (const f of BYTE_FIELDS) {
    const v = s[f];
    if (isBytes(v)) rec[f] = await encryptBytes(key, v);
  }
  return out;
}

/** Reverse {@link encStored}. Legacy plaintext rows (no `enc` flag) pass through unchanged. */
async function decStored(s: (StoredMockup & { enc?: true }) | null): Promise<StoredMockup | null> {
  if (!s || !s.enc) return s; // legacy plaintext row
  const key = await getBlobKey();
  if (!key) return s; // can't decrypt without the key
  const out = { ...s } as StoredMockup & { enc?: true };
  delete out.enc;
  const src = s as unknown as Record<string, unknown>;
  const rec = out as unknown as Record<string, unknown>;
  for (const f of BYTE_FIELDS) {
    const v = src[f];
    if (isBytes(v)) rec[f] = await decryptBytes(key, v);
  }
  return out;
}

/** Persist the baked mockup assets for a template version + view (memory + IndexedDB). The
 *  `view` param is additive — existing single-view callers keep persisting the primary view. */
export async function putMockup(
  versionId: string,
  assets: MockupAssets,
  view: string = PRIMARY_VIEW,
): Promise<void> {
  const stored: StoredMockup = {
    generated: assets.generated,
    shell: assets.shell,
    productMask: assets.productMask,
    printMask: assets.printMask,
    printQuad: assets.printQuad,
    clearPhone: !!assets.clearPhone,
    phone: assets.phone ?? null,
    texture: assets.texture ?? null,
    opacity: assets.opacity,
    blendMode: assets.blendMode,
    varnish: assets.varnish,
    designRotation: assets.designRotation,
    fillFit: assets.fillFit,
    designAspect: assets.designAspect,
    printShadingStrength: assets.printShadingStrength,
    shading: assets.shading,
  };
  const key = mockupKey(versionId, view);
  memSet(key, stored); // plaintext in RAM (fast render resolution), byte-budgeted LRU
  // `idb()` REJECTS on an open failure and resolves null only when there is no IndexedDB at
  // all, so the two cases are genuinely different:
  //
  //   null    — this runtime has no IndexedDB. A capability, not a fault. Silent.
  //   throws  — the store is BROKEN (blocked site data, private window, full disk). F-9: the
  //             write cannot happen, but the memory cache above already holds the bytes, so
  //             the session is fine and the run must not die. Degrade, and RECORD it so the
  //             surfaces that start runs can say the mock-ups are RAM-only.
  try {
    const db = await idb();
    if (!db) return;
    await idbPut(db, key, await encStored(stored)); // ciphertext on disk
  } catch (e) {
    noteDegraded(e);
  }
}

/**
 * Resolve baked mockup assets for a version + view (memory → IndexedDB), or null when none.
 * This is the function the QueueManager's `resolveMockup` is wired to.
 *
 * LEGACY FALLBACK: a `getMockup(id, "back")` miss also tries the BARE `versionId` key, where
 * pre-B2 builds wrote the (then always back-only) row. A hit is migrated into the per-view mem
 * cache so subsequent `hasMockup(id, "back")` fast paths see it. The fallback is primary-view
 * only — a non-primary view must never resolve to the legacy row.
 */
export async function getMockup(
  versionId: string,
  view: string = PRIMARY_VIEW,
): Promise<MockupAssets | null> {
  const key = mockupKey(versionId, view);
  let s = memGet(key);
  // F-9: MEMORY FIRST, and a broken store never prevents a hit here — a device whose
  // IndexedDB is blocked still renders the bake it downloaded this session.
  if (!s) {
    try {
      const db = await idb();
      if (db) {
        s = await decStored(await idbGet(db, key)); // decrypt the on-disk copy
        if (s) memSet(key, s);
      }
      // Legacy fallback (primary view only): a pre-B2 row lives under the bare `versionId` key.
      if (!s && view === PRIMARY_VIEW && db) {
        s = await decStored(await idbGet(db, versionId));
        if (s) memSet(key, s); // migrate into the new per-view key so `hasMockup` sees it
      }
    } catch (e) {
      // The store is broken AND memory has nothing, so this read cannot be answered. It must
      // still THROW: answering `null` means "this template has no bake", and the queue renders
      // that as a design over a blank background and calls the run a success
      // (`queue/manager.ts:616-624` is built on exactly that distinction).
      throw new Error(`mockup read failed and nothing is cached in memory: ${noteDegraded(e)}`);
    }
  }
  if (!s) return null;
  return {
    generated: s.generated,
    shell: s.shell,
    productMask: s.productMask,
    printMask: s.printMask,
    printQuad: s.printQuad,
    clearPhone: s.clearPhone,
    phone: s.phone ?? null,
    texture: s.texture ?? null,
    opacity: s.opacity,
    blendMode: s.blendMode,
    varnish: s.varnish,
    designRotation: s.designRotation,
    fillFit: s.fillFit,
    designAspect: s.designAspect,
    printShadingStrength: s.printShadingStrength,
    shading: withPlatformLight(s.shading),
  };
}

/** Whether baked assets exist for a version + view (memory-cache fast path). */
export function hasMockup(versionId: string, view: string = PRIMARY_VIEW): boolean {
  // WS-H2: `present`, not `mem` — an EVICTED bake is still on this device, and answering
  // "no bake" would re-trigger a multi-MB download (`bakedSync`) or a re-bake (`MaskEditor`).
  return present.has(mockupKey(versionId, view));
}

/**
 * Whether baked assets exist for a version + view ANYWHERE locally — memory OR the
 * persisted IndexedDB row. Unlike `getMockup` this never decodes/decrypts (a bare
 * `getKey` probe), so the auto-sync heal pass can check every synced template each tick
 * without pulling megabytes through AES-GCM. After an app restart the memory cache is
 * cold; this is the check that knows the download already happened.
 */
export async function hasMockupStored(versionId: string, view: string = PRIMARY_VIEW): Promise<boolean> {
  const key = mockupKey(versionId, view);
  if (present.has(key)) return true;
  // A presence PROBE answers "is it there", so an unopenable store answers "no" rather than
  // throwing — the caller's next move (download it) is the right one either way. The READ
  // (`getMockup`) is where an open failure must surface, because there it is the difference
  // between "no bake" and "a bake we could not load", and the queue already fails loud on it.
  const db = await idb().catch(() => null);
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const r = db.transaction(STORE, "readonly").objectStore(STORE).getKey(key);
      r.onsuccess = () => resolve(r.result != null);
      r.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/**
 * Delete ONE version+view's locally-cached bake — the in-memory entry AND its IndexedDB row.
 *
 * ED-12 (residual): removing a view tombstones its CLOUD refs, but without this the LOCAL bake
 * survived, so `hasMockup`/`getMockup` still answered for the removed view — re-adding it
 * resurrected the matte/texture the operator deliberately deleted, and a render composited over
 * the old shell. Also the collector for "remove from device" (see `removeSyncedTemplate`), which
 * otherwise leaves multi-MB rows nothing ever reclaims.
 *
 * Deleting the PRIMARY view also drops the pre-B2 BARE-`versionId` row, because `getMockup`
 * falls back to it for that view — leaving it would revive the bake through the back door.
 * Best-effort, mirroring {@link purgeAllMockups}: never throws into the caller.
 */
export async function deleteMockup(versionId: string, view: string = PRIMARY_VIEW): Promise<void> {
  const keys = [mockupKey(versionId, view)];
  if (view === PRIMARY_VIEW) keys.push(versionId); // legacy bare key (see getMockup's fallback)
  for (const k of keys) memDelete(k); // presence dropped too — ED-12
  const db = await idb().catch(() => null); // best-effort: never throws into the caller
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      for (const k of keys) tx.objectStore(STORE).delete(k);
      tx.oncomplete = () => resolve();
      tx.onabort = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Delete EVERY locally-cached baked mockup for this device — the in-memory map and the
 * whole IndexedDB store. Called on sign-out: these are multi-MB product renders belonging
 * to the account that just left, and on a shared machine the next account could otherwise
 * render from (and export) them. Also the only way to clear rows orphaned by a lost
 * encryption key. Best-effort: never throws into the sign-out path.
 */
export async function purgeAllMockups(): Promise<void> {
  memClear();
  const db = await idb().catch(() => null); // best-effort: never throws into the sign-out path
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onabort = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}
